/**
 * Advanced Excel utilities using ExcelJS for image support and professional formatting
 * Requires: npm install exceljs
 */

import mjLogoPath from "../assets/mnj-logo.jpg";
import { getLogoBuffer } from "./logoHelper";
import { createEndCover, CoverSheetData } from "./excelCoverSheet";

/**
 * Convert image URL to buffer using canvas (fallback method)
 */
const getImageBufferViaCanvas = async (imagePath: string): Promise<Buffer> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = img.width;
        canvas.height = img.height;
        
        ctx?.drawImage(img, 0, 0);
        
        // Convert to blob then to buffer
        canvas.toBlob(async (blob) => {
          if (blob) {
            const arrayBuffer = await blob.arrayBuffer();
            console.log('✅ Logo loaded via canvas, size:', arrayBuffer.byteLength, 'bytes');
            resolve(Buffer.from(arrayBuffer));
          } else {
            reject(new Error('Failed to convert canvas to blob'));
          }
        }, 'image/jpeg', 0.95);
        
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = (error) => {
      console.error('Image load error:', error);
      reject(new Error('Failed to load image via canvas method'));
    };
    
    img.src = imagePath;
  });
};

// Types for ExcelJS integration
export interface ExcelJSColumn {
  key: string;
  title: string;
  width?: number;
  render?: (value: any, record?: any) => string | number;
}

export interface ExcelJSExportOptions {
  filename: string;
  sheetName?: string;
  columns: ExcelJSColumn[];
  data: any[];
  includeLogo?: boolean;
  projectName?: string;
  reportTitle?: string;
  includeEndCover?: boolean;
  clientName?: string;
  siteAddress?: string;
  dateCreated?: Date;
  jobType?: "Groundworks" | "Roads & Sewers" | "Combined";
}

export interface ExcelJSMultiSheetOptions {
  filename: string;
  includeLogo?: boolean;
  projectName?: string;
  sheets: {
    name: string;
    columns: ExcelJSColumn[];
    data: any[];
    reportTitle?: string;
  }[];
}

/**
 * Convert image file to buffer for ExcelJS with multiple fallback methods
 */
const getImageBuffer = async (imagePath: string): Promise<Buffer> => {
  console.log('🔄 Attempting to load logo:', imagePath);
  
  // Method 1: Try direct fetch
  try {
    console.log('📥 Trying fetch method...');
    const response = await fetch(imagePath);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const arrayBuffer = await response.arrayBuffer();
    
    if (arrayBuffer.byteLength === 0) {
      throw new Error('Empty image data received');
    }
    
    console.log('✅ Logo loaded via fetch, size:', arrayBuffer.byteLength, 'bytes');
    return Buffer.from(arrayBuffer);
    
  } catch (fetchError) {
    console.warn('❌ Fetch method failed:', fetchError);
    
    // Method 2: Try absolute URL
    try {
      console.log('📥 Trying absolute URL method...');
      const baseUrl = window.location.origin;
      const absoluteUrl = new URL(imagePath, baseUrl).href;
      console.log('🔗 Absolute URL:', absoluteUrl);
      
      const response = await fetch(absoluteUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      
      if (arrayBuffer.byteLength === 0) {
        throw new Error('Empty image data received');
      }
      
      console.log('✅ Logo loaded via absolute URL, size:', arrayBuffer.byteLength, 'bytes');
      return Buffer.from(arrayBuffer);
      
    } catch (absoluteError) {
      console.warn('❌ Absolute URL method failed:', absoluteError);
      
      // Method 3: Try canvas method
      try {
        console.log('📥 Trying canvas method...');
        return await getImageBufferViaCanvas(imagePath);
      } catch (canvasError) {
        console.error('❌ Canvas method failed:', canvasError);
        
        // Method 4: Try with different paths
        const pathVariants = [
          imagePath,
          imagePath.replace('../assets/', '/src/assets/'),
          imagePath.replace('../assets/', '/assets/'),
          '/src/assets/mnj-logo.jpg',
          '/assets/mnj-logo.jpg',
          './src/assets/mnj-logo.jpg'
        ];
        
        for (const variant of pathVariants) {
          try {
            console.log('📥 Trying path variant:', variant);
            const response = await fetch(variant);
            if (response.ok) {
              const arrayBuffer = await response.arrayBuffer();
              if (arrayBuffer.byteLength > 0) {
                console.log('✅ Logo loaded via path variant:', variant, 'size:', arrayBuffer.byteLength, 'bytes');
                return Buffer.from(arrayBuffer);
              }
            }
          } catch (variantError) {
            // Continue to next variant
            console.log('⚠️ Path variant failed:', variant);
          }
        }
        
        // All methods failed
        throw new Error(`All logo loading methods failed. Original fetch error: ${fetchError}`);
      }
    }
  }
};

/**
 * Export data to Excel with embedded logo using ExcelJS
 */
export const exportToExcelWithLogo = async (options: ExcelJSExportOptions): Promise<void> => {
  try {
    // Dynamic import of ExcelJS to avoid bundle issues if not installed
    const ExcelJS = await import('exceljs');
    
    const {
      filename,
      sheetName = "Sheet1",
      columns,
      data,
      includeLogo = true,
      projectName,
      reportTitle = "Bill of Quantities Summary",
      includeEndCover = false,
      clientName,
      siteAddress,
      dateCreated,
      jobType = "Roads & Sewers"
    } = options;

    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    let currentRow = 1;

    // Add logo if enabled
    if (includeLogo) {
      try {
        console.log('🎨 Adding logo to Excel worksheet...');
        
        // Try the reliable method first
        let logoBuffer: Buffer;
        try {
          logoBuffer = await getLogoBuffer();
        } catch (reliableMethodError) {
          console.warn('Reliable method failed, trying fallback:', reliableMethodError);
          logoBuffer = await getImageBuffer(mjLogoPath);
        }
        
        const imageId = workbook.addImage({
          buffer: logoBuffer,
          extension: 'jpeg',
        });

        // Add logo to worksheet (positioned at A1 with small padding)
        worksheet.addImage(imageId, {
          tl: { col: 0.15, row: 0.1 },
          ext: { width: 150, height: 60 }
        });

        worksheet.getRow(1).height = 128;

        // Merge row 1 from A to F and apply dark branded background
        worksheet.mergeCells('A1:F1');
        const logoHeaderCell = worksheet.getCell('A1');
        logoHeaderCell.value = 'M & J EVANS CONSTRUCTION LIMITED';
        logoHeaderCell.font = { name: 'Calibri', bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
        logoHeaderCell.alignment = { horizontal: 'right', vertical: 'middle' };
        logoHeaderCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0A2642' } };

        worksheet.getCell('F2').value = 'The Groundworks Partner of Choice';
        worksheet.getCell('F2').font = { name: 'Calibri', size: 11, color: { argb: '64748b' } };

        console.log('✅ Logo successfully added to Excel worksheet');
        // Skip more rows for logo space to prevent overlap
        currentRow = 7;
      } catch (error) {
        console.error('❌ Could not add logo to Excel:', error);
        console.log('📄 Continuing export without logo...');
        // Continue without logo
      }
    }

    // Add report title
    if (reportTitle) {
      const titleRow = currentRow;
      worksheet.getCell(`A${titleRow}`).value = reportTitle.toUpperCase();
      worksheet.getCell(`A${titleRow}`).font = { name: 'Calibri', bold: true, size: 14, color: { argb: '1e3a8a' } };
      worksheet.getCell(`A${titleRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: false };
      worksheet.mergeCells(titleRow, 1, titleRow, columns.length);
      currentRow += 2;
    }

    // Add project information
    if (projectName) {
      const projectRow = currentRow;
      worksheet.getCell(`A${projectRow}`).value = `Project: ${projectName}`;
      worksheet.getCell(`A${projectRow}`).font = { name: 'Calibri', bold: true, size: 11 };
      worksheet.getCell(`A${projectRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: false };
      worksheet.mergeCells(projectRow, 1, projectRow, columns.length);
      currentRow++;
    }

    // Add generation date
    const dateRow = currentRow;
    worksheet.getCell(`A${dateRow}`).value = `Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
    worksheet.getCell(`A${dateRow}`).font = { name: 'Calibri', size: 10, color: { argb: '64748b' } };
    worksheet.getCell(`A${dateRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: false };
    worksheet.mergeCells(dateRow, 1, dateRow, columns.length);
    currentRow += 2;

    // Store the header row number for later reference
    const headerRowNumber = currentRow;

    // Add column headers
    const headerRow = worksheet.getRow(currentRow);
    columns.forEach((col, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = col.title;
      cell.font = { name: 'Calibri', bold: true, color: { argb: 'FFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '1e40af' }
      };
      cell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: true
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });
    currentRow++;

    // Store data start row
    const dataStartRow = currentRow;

    // Add data rows
    data.forEach((item, rowIndex) => {
      const dataRow = worksheet.getRow(currentRow + rowIndex);

      // Calculate row height based on content (will be set after cells are populated)
      let maxHeight = 26; // Minimum height

      columns.forEach((col, colIndex) => {
        const cell = dataRow.getCell(colIndex + 1);
        const value = getNestedValue(item, col.key);
        cell.value = col.render ? col.render(value, item) : value;

        // Calculate height for text content in description column
        if (cell.value && typeof cell.value === 'string' && colIndex === 0) {
          const lineCount = (cell.value.match(/\n/g) || []).length + 1;
          const charsPerLine = 40; // Realistic estimate for description column
          const estimatedWrappedLines = Math.ceil(cell.value.length / charsPerLine);
          const totalLines = Math.max(lineCount, estimatedWrappedLines);
          const extraPadding = 6;
          const calculatedHeight = Math.max(26, (totalLines * 16) + extraPadding);
          maxHeight = Math.max(maxHeight, calculatedHeight);
        }

        // Determine alignment based on column type
        let horizontalAlign: 'left' | 'center' | 'right' = 'center';
        if (colIndex === 0) {
          horizontalAlign = 'left'; // Description column
        } else if (col.key === 'quantity' || col.key === 'rate' || col.key === 'amount' || col.key === 'ohp') {
          horizontalAlign = 'right'; // Numbers and currency
        }

        // Enable text wrapping for all cells (especially description)
        cell.alignment = {
          vertical: 'top',
          horizontal: horizontalAlign,
          wrapText: true,
          shrinkToFit: false
        };

        // Set explicit font size to ensure wrapping works
        if (!cell.font) {
          cell.font = { name: 'Calibri', size: 11 };
        }

        // Styling for data cells
        cell.border = {
          top: { style: 'thin', color: { argb: 'E5E7EB' } },
          left: { style: 'thin', color: { argb: 'E5E7EB' } },
          bottom: { style: 'thin', color: { argb: 'E5E7EB' } },
          right: { style: 'thin', color: { argb: 'E5E7EB' } }
        };

        // Alternate row coloring
        if (rowIndex % 2 === 0) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F9FAFB' }
          };
        }
      });

      // Set the calculated row height
      dataRow.height = maxHeight;
    });

    const dataEndRow = currentRow + data.length - 1;

    // Set fixed column widths optimized for single page printing
    // These widths apply to the table columns
    columns.forEach((col, index) => {
      const column = worksheet.getColumn(index + 1);
      // Use specified width or calculate based on column type
      if (col.width) {
        column.width = col.width;
      } else {
        // Default small widths for non-description columns
        column.width = 10;
      }
    });

    // Set page setup for printing - force fit to one page width
    worksheet.pageSetup = {
      paperSize: 9, // A4
      orientation: 'landscape',
      fitToPage: true,
      fitToWidth: 1, // All columns must fit on one page width
      fitToHeight: 0, // Allow multiple pages vertically if needed
      scale: 95, // Slightly reduce scale to ensure better fit
      margins: {
        left: 0.3,
        right: 0.3,
        top: 0.4,
        bottom: 0.4,
        header: 0.15,
        footer: 0.15
      },
      printArea: undefined, // Print entire sheet
      printTitlesRow: `${headerRowNumber}:${headerRowNumber}`, // Repeat header row
      horizontalCentered: true, // Center content horizontally on page
    };

    // Set print options and view settings
    worksheet.properties = {
      ...worksheet.properties,
      defaultRowHeight: 15,
    };

    // Set view to show how it will print
    worksheet.views = [
      {
        state: 'normal',
        showGridLines: false, // Hide gridlines for cleaner print
      }
    ];

    // Add end cover if requested
    if (includeEndCover) {
      try {
        console.log('📄 Adding end cover sheet...');
        const coverData: CoverSheetData = {
          projectName: projectName || "Project Name",
          clientName: clientName || "CLIENT NAME",
          region: "West Midlands",
          jobType: jobType,
          siteAddress: siteAddress,
          dateCreated: dateCreated || new Date()
        };
        await createEndCover(workbook as any, coverData);
        console.log('✅ End cover sheet added successfully');
      } catch (error) {
        console.error('❌ Failed to add end cover:', error);
        console.log('📄 Continuing export without end cover...');
      }
    }

    // Save file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error('ExcelJS export failed:', error);
    if (error.message?.includes('Cannot resolve module')) {
      throw new Error('ExcelJS library not installed. Please run: npm install exceljs');
    }
    throw new Error(`Excel export failed: ${error.message}`);
  }
};

/**
 * Export multiple sheets to Excel with embedded logos
 */
export const exportMultiSheetWithLogo = async (options: ExcelJSMultiSheetOptions): Promise<void> => {
  try {
    const ExcelJS = await import('exceljs');
    
    const {
      filename,
      includeLogo = true,
      projectName,
      sheets
    } = options;

    const workbook = new ExcelJS.Workbook();
    let logoBuffer: Buffer | null = null;

    // Load logo once if needed
    if (includeLogo) {
      try {
        console.log('📊 Loading logo for multi-sheet export...');
        
        // Try the reliable method first
        try {
          logoBuffer = await getLogoBuffer();
        } catch (reliableMethodError) {
          console.warn('Reliable method failed for multi-sheet, trying fallback:', reliableMethodError);
          logoBuffer = await getImageBuffer(mjLogoPath);
        }
        
        console.log('✅ Logo loaded for multi-sheet export');
      } catch (error) {
        console.error('❌ Could not load logo for multi-sheet export:', error);
      }
    }

    // Process each sheet
    for (const sheet of sheets) {
      const worksheet = workbook.addWorksheet(sheet.name);
      let currentRow = 1;

      // Add logo to each sheet
      if (includeLogo && logoBuffer) {
        try {
          const imageId = workbook.addImage({
            buffer: logoBuffer,
            extension: 'jpeg',
          });

          worksheet.addImage(imageId, {
            tl: { col: 0.15, row: 0.1 },
            ext: { width: 150, height: 60 }
          });

          // Company info (moved further right to avoid overlap)
          worksheet.getCell('F1').value = 'M & J EVANS CONSTRUCTION LIMITED';
          worksheet.getCell('F1').font = { name: 'Calibri', bold: true, size: 14, color: { argb: '1e3a8a' } };

          worksheet.getCell('F2').value = 'The Groundworks Partner of Choice';
          worksheet.getCell('F2').font = { name: 'Calibri', size: 11, color: { argb: '64748b' } };

          currentRow = 7;
        } catch (error) {
          console.warn(`Could not add logo to sheet ${sheet.name}:`, error);
        }
      }

      // Add sheet content similar to single sheet export
      if (sheet.reportTitle) {
        worksheet.getCell(`A${currentRow}`).value = sheet.reportTitle.toUpperCase();
        worksheet.getCell(`A${currentRow}`).font = { name: 'Calibri', bold: true, size: 14 };
        currentRow += 2;
      }

      if (projectName) {
        worksheet.getCell(`A${currentRow}`).value = `Project: ${projectName}`;
        worksheet.getCell(`A${currentRow}`).font = { name: 'Calibri', bold: true, size: 11 };
        currentRow++;
      }

      currentRow += 1;

      // Headers and data (similar to single sheet)
      const headerRow = worksheet.getRow(currentRow);
      sheet.columns.forEach((col, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = col.title;
        cell.font = { name: 'Calibri', bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '1e40af' }
        };
        cell.alignment = { horizontal: 'center' };
      });
      currentRow++;

      // Add data
      sheet.data.forEach((item, rowIndex) => {
        const dataRow = worksheet.getRow(currentRow + rowIndex);
        let maxHeight = 26; // Minimum height

        sheet.columns.forEach((col, colIndex) => {
          const cell = dataRow.getCell(colIndex + 1);
          const value = getNestedValue(item, col.key);
          cell.value = col.render ? col.render(value, item) : value;

          // Calculate height for text content in description column
          if (cell.value && typeof cell.value === 'string' && colIndex === 0) {
            const lineCount = (cell.value.match(/\n/g) || []).length + 1;
            const charsPerLine = 40; // Realistic estimate for description column
            const estimatedWrappedLines = Math.ceil(cell.value.length / charsPerLine);
            const totalLines = Math.max(lineCount, estimatedWrappedLines);
            const extraPadding = 6;
            const calculatedHeight = Math.max(26, (totalLines * 16) + extraPadding);
            maxHeight = Math.max(maxHeight, calculatedHeight);
          }
        });

        // Set the calculated row height
        dataRow.height = maxHeight;
      });

      // Set column widths - minimal for single page printing
      sheet.columns.forEach((col, index) => {
        const column = worksheet.getColumn(index + 1);
        column.width = col.width || 10; // Reduced default from 15 to 10
      });

      // Enable text wrapping and page setup for each sheet
      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.alignment = {
            ...cell.alignment,
            wrapText: true,
            vertical: 'top',
            shrinkToFit: false
          };
          // Ensure font is set to enable proper wrapping
          if (!cell.font || !cell.font.size) {
            cell.font = { ...cell.font, name: 'Calibri', size: 11 };
          }
        });
      });

      worksheet.pageSetup = {
        paperSize: 9,
        orientation: 'landscape',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        scale: 95, // Slightly reduce scale for better fit
        margins: {
          left: 0.3,
          right: 0.3,
          top: 0.4,
          bottom: 0.4,
          header: 0.15,
          footer: 0.15
        },
        horizontalCentered: true,
      };
    }

    // Save file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Multi-sheet ExcelJS export failed:', error);
    throw error;
  }
};

/**
 * Enhanced Bill Summary export with logo using ExcelJS
 */
export const exportBillSummaryWithLogo = async (options: ExcelJSExportOptions): Promise<void> => {
  const enhancedOptions = {
    ...options,
    includeLogo: true,
    reportTitle: options.reportTitle || "Bill of Quantities Summary"
  };
  
  return exportToExcelWithLogo(enhancedOptions);
};

// Helper function to get nested values
const getNestedValue = (obj: any, path: string) => {
  return path.split(".").reduce((acc, key) => acc && acc[key], obj);
};

/**
 * Fallback function that uses XLSX if ExcelJS is not available
 */
export const exportWithLogoFallback = async (options: ExcelJSExportOptions): Promise<void> => {
  try {
    // Try ExcelJS first
    await exportToExcelWithLogo(options);
  } catch (error) {
    console.warn('ExcelJS not available, falling back to XLSX with text branding');
    
    // Fallback to enhanced XLSX export
    const { exportToExcel } = await import('./excelUtils');
    const xlsxOptions = {
      filename: options.filename,
      sheetName: options.sheetName || "Sheet1",
      columns: options.columns.map(col => ({
        key: col.key,
        title: col.title,
        render: col.render
      })),
      data: options.data,
      includeBranding: true,
      projectName: options.projectName
    };
    
    return exportToExcel(xlsxOptions);
  }
};

/**
 * Simple wrapper to convert existing excelUtils columns to ExcelJS format
 */
export const convertColumnsForExcelJS = (columns: any[]): ExcelJSColumn[] => {
  return columns.map(col => ({
    key: col.key,
    title: col.title,
    width: col.width || 15,
    render: col.render
  }));
};

/**
 * Enhanced export function for any bill summary data
 */
export const exportBillSummaryData = async (
  data: any[],
  columns: any[],
  filename: string,
  sheetName: string = "Bill Summary",
  projectName?: string
): Promise<void> => {
  const excelJSColumns = convertColumnsForExcelJS(columns);
  
  return exportWithLogoFallback({
    filename,
    sheetName,
    columns: excelJSColumns,
    data,
    includeLogo: true,
    projectName,
    reportTitle: sheetName
  });
};